export class CustomSelect {
  constructor({ selectContainer, onChange }) {
    this._selectContainer = selectContainer;
    this._selectInput = this._selectContainer.querySelector('.dropdown-toggle');
    this._options = this._selectContainer.querySelectorAll('.dropdown-item');
    this._hiddenInput = this._selectContainer.querySelector('input');
    this._onChange = onChange;
    this._inputField = null;
    this._scrollContainer = null;
    this._currentIndex = 0;
    this._optionData = []; // Массив для хранения данных всех опций
    this._filteredOptions = []; // Массив для фильтруемых опций
    this._selectHighlightedOption = this._selectHighlightedOption.bind(this);
    this._init();
  }

  _init() {
    // Инициализируем массив данных для всех опций
    this._optionData = Array.from(this._options).map((option) => ({
      value: option.dataset.value,
      text: option.innerText
    }));

    // Инициализируем события
    this._selectInput.addEventListener('click', () => this._openDropdown());

    this._itemHeight = 30; // Предполагаемая высота элемента
    this._buffer = 10; // Количество дополнительных элементов для рендеринга
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.dropdown') && this._selectInput !== e.target && !e.target.closest('.dropdown-menu')) {
        this._closeDropdown();
      }
    });

    // Если у контейнера есть атрибут onchange, навешиваем обработчик на inputField
    const onchangeAttr = this._selectContainer.getAttribute('onchange');
    if (onchangeAttr) {
      this._hiddenInput.addEventListener('change', function (e) {
        // Выполняем код из атрибута onchange в контексте контейнера
        // eslint-disable-next-line no-new-func
        new Function(onchangeAttr).call(this, e);
      });
      this._selectContainer.removeAttribute('onchange');
    }
    if (this._onChange) {
      this._hiddenInput.addEventListener('change', this._onChange);
    }
    this._handleKeydown = this._handleKeydown.bind(this);
  }

  _openDropdown() {
    document.addEventListener('keydown', this._handleKeydown);

    this._inputField = document.createElement('input');
    const inputField = this._inputField;
    inputField.type = 'text';
    inputField.classList.add('form-control-active', 'form-control');
    inputField.value = this._selectInput.innerText.trim();
    this._selectInput.replaceWith(inputField); // Заменяем кнопку на input

    // Создаем scroll-container
    const scrollContainer = document.createElement('div');
    scrollContainer.classList.add('dropdown-menu', 'scroll-container');
    scrollContainer.style.maxHeight = '200px';
    scrollContainer.style.overflowY = 'auto';
    scrollContainer.style.position = 'absolute';
    scrollContainer.style.display = 'block';

    // ВАЖНО: ширину нужно ставить после добавления в DOM или вычислять только если она ненулевая
    if (this._selectContainer.offsetWidth > 0) {
      scrollContainer.style.width = `${this._selectContainer.offsetWidth}px`;
    } else {
      scrollContainer.style.width = '100%'; // Fallback
    }

    scrollContainer.style.left = 0; // Позиция по оси X

    // Распорка должна быть В ПОТОКЕ, чтобы создавать скролл
    this._virtualSpacer = document.createElement('div');
    this._virtualSpacer.style.height = '0px';
    this._virtualSpacer.style.width = '1px';
    this._virtualSpacer.style.opacity = '0';
    this._virtualSpacer.style.pointerEvents = 'none';

    // Контейнер для отображаемых элементов - абсолютно позиционирован
    this._virtualContent = document.createElement('div');
    this._virtualContent.classList.add('scroll-content');
    this._virtualContent.style.position = 'absolute';
    this._virtualContent.style.top = '0';
    this._virtualContent.style.left = '0';
    this._virtualContent.style.width = '100%';
    this._virtualContent.style.transform = 'translateY(0px)';

    this._filteredOptions = [...this._optionData];
    this._scrollContainer = scrollContainer;

    scrollContainer.appendChild(this._virtualSpacer);
    scrollContainer.appendChild(this._virtualContent);
    this._selectContainer.appendChild(scrollContainer);

    // Дополнительный вызов рендера через RAF после добавления в DOM, чтобы размеры просчитались
    requestAnimationFrame(() => {
      if (this._selectContainer.offsetWidth > 0) {
        scrollContainer.style.width = `${this._selectContainer.offsetWidth}px`;
      }
      this._renderOptions();
    });

    // Инициализация событий скролла
    scrollContainer.addEventListener('scroll', () => this._onScroll());

    this._renderOptions(); // Первый рендер

    // Добавляем обработчик для фильтрации
    requestAnimationFrame(() => {
      inputField.addEventListener('input', () => this._filterOptions(inputField.value));

      inputField.focus();
      inputField.select();
    });
    this._highlightOption(0);
  }

  _onScroll() {
    requestAnimationFrame(() => this._renderVisibleItems());
  }

  _filterOptions(filterText) {
    const lowerCaseFilter = filterText.toLowerCase();

    // Фильтруем опции в памяти
    this._filteredOptions = this._optionData.filter((option) => option.text.toLowerCase().includes(lowerCaseFilter));
    this._scrollContainer.scrollTop = 0; // Сброс скролла при фильтрации
    this._renderOptions(); // Обновляем верстку после фильтрации
  }

  _renderOptions() {
    // Обновляем высоту распорки
    const totalHeight = this._filteredOptions.length * this._itemHeight;
    this._virtualSpacer.style.height = `${totalHeight}px`;

    // Рендерим видимые элементы
    this._renderVisibleItems();
  }

  _renderVisibleItems() {
    const scrollTop = this._scrollContainer.scrollTop;
    const containerHeight = this._scrollContainer.clientHeight || 200; // Высота контейнера (фоллбэк 200)

    // Расчет индексов
    let startNode = Math.floor(scrollTop / this._itemHeight) - this._buffer;
    startNode = Math.max(0, startNode);

    let visibleNodeCount = Math.ceil(containerHeight / this._itemHeight) + 2 * this._buffer;
    visibleNodeCount = Math.min(this._filteredOptions.length - startNode, visibleNodeCount);

    if (visibleNodeCount < 0) visibleNodeCount = 0;

    const offsetY = startNode * this._itemHeight;
    this._virtualContent.style.transform = `translateY(${offsetY}px)`;
    // Используем абсолютное позиционирование контейнера контента внутри скролла, чтобы он "плавал"

    // Очищаем и рендерим
    this._virtualContent.innerHTML = '';

    const fragment = document.createDocumentFragment();
    for (let i = startNode; i < startNode + visibleNodeCount; i++) {
      const option = this._filteredOptions[i];
      if (!option) continue;

      const optionElement = document.createElement('button');
      optionElement.classList.add('dropdown-item');
      optionElement.style.height = `${this._itemHeight}px`; // Явно задаем высоту для соответствия расчетам
      optionElement.style.overflow = 'hidden';
      optionElement.style.textOverflow = 'ellipsis';
      optionElement.style.whiteSpace = 'nowrap';
      // optionElement.style.display = 'block'; // Bootstrap dropdown-item is usually block

      optionElement.dataset.value = option.value;
      optionElement.dataset.index = i; // Сохраняем реальный индекс для подсветки
      optionElement.innerText = option.text;

      if (i === this._currentIndex) {
        optionElement.classList.add('active');
      }

      optionElement.addEventListener('click', (e) => this._selectOption(e));
      fragment.appendChild(optionElement);
    }
    this._virtualContent.appendChild(fragment);
  }

  _selectOption(event) {
    const selectedOption = event.target;
    this._doSelect(selectedOption.dataset.value, selectedOption.innerText);
  }

  _doSelect(value, text) {
    this._hiddenInput.value = value;
    this._selectInput.innerText = text;

    // Пробуем вызвать updateData, если он есть
    const fieldset = this._hiddenInput.closest('fieldset');
    if (fieldset && typeof fieldset.updateData === 'function') {
      fieldset.updateData();
    }

    const changeEvent = new Event('change', { bubbles: true });
    this._hiddenInput.dispatchEvent(changeEvent);

    this._closeDropdown();
  }

  _closeDropdown() {
    if (this._inputField) {
      this._inputField.replaceWith(this._selectInput); // Удаляем input
    }

    if (this._scrollContainer) {
      this._scrollContainer.remove(); // Удаляем scroll-container
    }

    document.removeEventListener('keydown', this._handleKeydown);
  }

  _handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this._closeDropdown();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this._highlightOption(this._currentIndex + 1); // Переход к следующему элементу
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this._highlightOption(this._currentIndex - 1); // Переход к предыдущему элементу
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this._selectHighlightedOption(); // Выбор текущего выделенного элемента
    }
  }

  _highlightOption(index) {
    if (this._filteredOptions.length === 0) return;

    if (index >= this._filteredOptions.length) {
      index = 0;
    } else if (index < 0) {
      index = this._filteredOptions.length - 1;
    }

    this._currentIndex = index;

    // Снимаем подсветку со всех
    const allItems = this._virtualContent.querySelectorAll('.dropdown-item');
    allItems.forEach(item => item.classList.remove('active'));

    // Проверяем, отрендерен ли элемент
    const activeItem = this._virtualContent.querySelector(`.dropdown-item[data-index="${index}"]`);

    if (activeItem) {
      activeItem.classList.add('active');
      // Если элемент есть, проверим видимость и подскроллим минимально
      const itemTop = index * this._itemHeight;
      const itemBottom = itemTop + this._itemHeight;
      const scrollTop = this._scrollContainer.scrollTop;
      const clientHeight = this._scrollContainer.clientHeight;

      if (itemTop < scrollTop) {
        this._scrollContainer.scrollTop = itemTop;
      } else if (itemBottom > scrollTop + clientHeight) {
        this._scrollContainer.scrollTop = itemBottom - clientHeight;
      }
    } else {
      // Элемента нет в DOM -> скроллим к нему
      // Центрируем или просто показываем сверху? Лучше чтобы стал видимым.
      // Простой вариант: поставить его в начало видимой области
      this._scrollContainer.scrollTop = index * this._itemHeight;

      // После скролла сработает _onScroll -> _renderVisibleItems, который сам проставит active для _currentIndex
    }
  }

  _selectHighlightedOption() {
    const option = this._filteredOptions[this._currentIndex];
    if (option) {
      this._doSelect(option.value, option.text);
    }
  }

  rebuildOptions(newOptions) {
    this._optionData = Object.entries(newOptions).map(([value, text]) => ({ value, text }));
    this._options = null; // можно обнулить, чтобы потом пересоздать
  }
}
